import { useMemo, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"

import { cursoCorto } from "../../../../../utils/cursoCorto"
import { horarioBonito } from "../../../../../utils/horarioBonito"
import { bloqueEn, cursoDeMatricula, esRotativoVisitante, esVisitanteEn, etiquetaTransferida, pastillaVisitante } from "../../../../../utils/visitante"
import Constants, { ENROLLMENT_STATUS } from "../../../../../constants"

import "./paseDeLista.css"
import { ahoraCdmx } from "../../../../../utils/horaMexico"

/*
	Pase de lista en telefono.

	La tabla de escritorio, en un telefono, se encogia a 10px y escondia columnas.
	Aqui cada alumno es una fila alta con dos botones.

	Decisiones de Esteban (2026-09-16):
	- Sin "todos presentes" y sin estado inicial. Un interruptor que ya viene en
	  una posicion es lo mismo que marcar a todos: se guarda sin mirar. Dos botones
	  vacios obligan a decidir uno por uno.
	- La barra de abajo dice solo cuantos faltan.
	- La foto de la clase se hace visible: ya era obligatoria para registrar una
	  asistencia nueva, pero la pantalla no lo decia y Guardar se quedaba gris sin
	  explicar por que.
*/

type Props =
{
	selectedSchedule    : any
	selectedSession     : any
	selectedAttendance  : any[]
	editableAttendance  : any
	toggleStudentAttendance : (enrollmentId : number) => void
	showNoteModal       : (matricula : any) => void
	idleLocked          : boolean
	isNewAttendance     : boolean
	uploadDocuments     : File[]
	fileChange          : (archivos : File[]) => void
	onGuardar           : () => void
	puedeGuardar        : boolean
	/** Quién ya recibió un toque; vive en el controlador para que el borrador lo guarde. */
	tocados             : {[id : number] : boolean}
	onTocar             : (enrollmentId : number) => void
	usuario?            : string
	onEstudianteExtra?  : () => void
	descargables?       : any[]
	onDescargar?        : (documento : any) => void
}

const PaseDeLista = ({
	selectedSchedule, selectedSession, selectedAttendance, editableAttendance,
	toggleStudentAttendance, showNoteModal, idleLocked, isNewAttendance,
	uploadDocuments, fileChange, onGuardar, puedeGuardar,
	tocados, onTocar,
	usuario, onEstudianteExtra, descargables, onDescargar
} : Props) =>
{
	/*
		Quien ya recibio un toque. En una asistencia que ya estaba registrada todos
		cuentan como decididos, porque el valor guardado ya es una decision; en una
		nueva se empieza de cero (o desde el borrador, si la pagina se recargo).
	*/
	const archivo = useRef<HTMLInputElement>(null)

	/*
		Cada foto nueva REEMPLAZABA a la anterior, asi que solo se podia subir una:
		las maestras tenian que tomar dos, una para la app y otra para el grupo.
		Ahora se acumulan. Ademas se limpia el valor del input para que se pueda
		volver a elegir el mismo archivo. (Esteban, 2026-09-16)
	*/
	/*
		El archivo llegaba con el nombre que le pone el telefono ("IMG_20260909_11...").
		Se renombra con lo que hace falta para saber de que es sin abrirlo: curso,
		dia de la sesion, quien la subio y a que hora exacta. (Esteban, 2026-09-16)
	*/
	const nombreDeFoto = (original : File) : string =>
	{
		const ahora = ahoraCdmx()
		const reloj = String(ahora.getHours()).padStart(2, "0") + "." +
			String(ahora.getMinutes()).padStart(2, "0")
		const extension = (original.name.split(".").pop() || "jpg").toLowerCase()

		const partes = [
			cursoCorto(selectedSchedule?.course),
			selectedSession?.sessionOn,
			usuario,
			reloj
		]
			.map(parte => String(parte || "").replace(/[\/\\:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim())
			.filter(Boolean)

		return partes.join(" - ") + "." + extension
	}

	const agregarFotos = (evento : React.ChangeEvent<HTMLInputElement>) =>
	{
		const nuevas = Array.from(evento.target.files || [])
			.map(archivo => new File([archivo], nombreDeFoto(archivo), {type : archivo.type}))

		if(nuevas.length > 0) fileChange([...(uploadDocuments || []), ...nuevas])

		evento.target.value = ""
	}

	/*
		Lleva etiqueta y va despues de los alumnos del horario: el rotativo que hoy
		viene de otro horario, o el de curso compuesto (su matricula es de Hoteleria
		aunque este horario de Gastronomia 1 sea el suyo ahora). (Esteban, 2026-09-17/18)
	*/
	const esVisitante = (matricula : any) : boolean =>
		esVisitanteEn(matricula, selectedSchedule?.scheduleId)

	/*
		Rotativo: "Hotelería · Domingo · 7:00 am – 11:00 am", de donde viene.
		Curso compuesto: "Bloque: Cocina fría", que es lo que toma aqui.
	*/
	const origenVisitante = (matricula : any) : string =>
	{
		if(esRotativoVisitante(matricula, selectedSchedule?.scheduleId))
		{
			return [cursoDeMatricula(matricula), horarioBonito(matricula?.homeScheduleName)].filter(Boolean).join(" · ")
		}

		const bloque = bloqueEn(matricula, selectedSchedule?.scheduleId)

		return bloque ? "Bloque: " + bloque : cursoDeMatricula(matricula)
	}

	const activos = useMemo
	(
		() => (selectedAttendance || [])
			.filter((m : any) => m.enrollmentStatusId === ENROLLMENT_STATUS.ACTIVE)
			.sort((a : any, b : any) => (esVisitante(a) ? 1 : 0) - (esVisitante(b) ? 1 : 0)),
		[selectedAttendance, selectedSchedule?.scheduleId]
	)

	//Al que no se le exige la clase (llegó por cambio de horario después) no hay nada que marcar.
	const estaDecidido = (matricula : any) : boolean =>
		matricula?.notRequired === true || !isNewAttendance || tocados[matricula.enrollmentId] === true

	const marcar = (matricula : any, presente : boolean) =>
	{
		const actual = editableAttendance?.[matricula.enrollmentId]?.isPresent === true

		if(actual !== presente) toggleStudentAttendance(matricula.enrollmentId)

		onTocar(matricula.enrollmentId)
	}

	const decididos = activos.filter(estaDecidido).length
	const presentes = activos.filter
		((m : any) => !m?.notRequired && estaDecidido(m) && editableAttendance?.[m.enrollmentId]?.isPresent === true).length

	const faltanPersonas = activos.length - decididos
	const faltaFoto      = isNewAttendance && (uploadDocuments || []).length === 0

	const recado =
		faltanPersonas > 0
			? "Faltan " + faltanPersonas + " alumno" + (faltanPersonas === 1 ? "" : "s")
			: (faltaFoto ? "Falta la foto" : "Todo listo")

	const habilitado = !idleLocked && puedeGuardar && faltanPersonas === 0 && !faltaFoto

	return <div className="waPase">
		<div className="waPaseCabecera">
			{
				!selectedSession ? null : <span className="waPaseChip">
					{selectedSession.sessionOn}
				</span>
			}
			<h3>
				{cursoCorto(selectedSchedule?.course)}
			</h3>
			<p>
				{horarioBonito(selectedSchedule?.name)}
				{activos.length > 0 ? " · " + activos.length + " alumnos" : ""}
			</p>
		</div>

		<div className="waPaseCuenta">
			<span>
				<b>{decididos}</b> de {activos.length}
				<small>marcados</small>
			</span>
			<span className="waPasePresentes">
				<b>{presentes}</b>
				<small>presentes</small>
			</span>
		</div>

		<div className="waPaseLista">
			{
				activos.map((matricula : any) =>
				{
					const editable  = editableAttendance?.[matricula.enrollmentId]
					const presente  = editable?.isPresent === true
					const decidido  = estaDecidido(matricula)
					const debe      = matricula?.generalBalance?.expiredMonthlyBalance?.value > 0
					const resumen   = editable?.attendanceSummary

					return <div className="waPaseFila" key={"PL_" + matricula.enrollmentId}>
						<div className="waPaseAlumno">
							<span className="waPaseNombre">
								{matricula.firstName} {matricula.lastName}
								{debe ? <span className="waPaseDebe">Debe</span> : null}
								{esVisitante(matricula) ? <span className="waPaseRotativo">{pastillaVisitante(matricula, selectedSchedule?.scheduleId)}</span> : null}
							</span>
							{
								!esVisitante(matricula) ? null :
									<span className="waPaseCasa">
										{origenVisitante(matricula)}
									</span>
							}
							{
								!matricula.transferredFrom ? null :
									<span className="waPaseTransferida">
										{etiquetaTransferida(matricula)}
									</span>
							}
							<span className="waPaseResumen">
								{
									!resumen ? "" :
										resumen.attendancePercentage + " · " +
										resumen.attendedSessions + " de " + resumen.totalSessions + " sesiones"
								}
							</span>
						</div>

						{
							matricula.notRequired
								?
							<div className="waPaseNoExige">{Constants.Common.notRequired}</div>
								:
						<div className="waPaseBotones">
							<button
								type="button"
								disabled={idleLocked}
								className={decidido && presente ? "waSi activo" : "waSi"}
								aria-pressed={decidido && presente}
								onClick={() => marcar(matricula, true)}
							>
								Presente
							</button>
							<button
								type="button"
								disabled={idleLocked}
								className={decidido && !presente ? "waNo activo" : "waNo"}
								aria-pressed={decidido && !presente}
								onClick={() => marcar(matricula, false)}
							>
								Ausente
							</button>
						</div>
						}

						<button
							type="button"
							className="waPaseNota"
							disabled={idleLocked}
							onClick={() => showNoteModal(editable)}
						>
							<FontAwesomeIcon icon={solid("note-sticky")} />
							{editable?.attendanceNotes ? " Ver nota" : " Agregar nota"}
						</button>
					</div>
				})
			}
		</div>

		{
			<div className="waPaseFoto">
				<input
					ref={archivo}
					type="file"
					accept="image/*"
					multiple
					style={{display : "none"}}
					onChange={agregarFotos}
				/>
				{
					(uploadDocuments || []).length === 0
						?
						<button type="button" className="waPaseFotoVacia" disabled={idleLocked}
							onClick={() => archivo.current?.click()}
						>
							<FontAwesomeIcon icon={solid("camera")} size="lg" />
							<span className="waPaseFotoTitulo">Foto de la clase</span>
							<span className="waPaseFotoNota">
								{isNewAttendance ? "Obligatoria. Puedes subir varias." : "Puedes subir varias."}
							</span>
						</button>
						:
						<div className="waPaseFotoLista">
							<span className="waPaseFotoMarca">
								<FontAwesomeIcon icon={solid("check")} />
							</span>
							<span className="waPaseFotoTexto">
								<b>{uploadDocuments.length === 1 ? "1 foto lista" : uploadDocuments.length + " fotos listas"}</b>
								<small>{uploadDocuments.map(f => f.name).join(", ").slice(0, 60)}</small>
							</span>
							<button type="button" disabled={idleLocked} onClick={() => archivo.current?.click()}>
								Agregar otra
							</button>
						</div>
				}
			</div>
		}

		<div className="waPaseExtras">
			{
				!onEstudianteExtra ? null :
					<button type="button" disabled={idleLocked} onClick={onEstudianteExtra}>
						<FontAwesomeIcon icon={solid("user-plus")} />
						Estudiante extra
					</button>
			}
			<button type="button" disabled={idleLocked} onClick={() => archivo.current?.click()}>
				<FontAwesomeIcon icon={solid("camera")} />
				{(uploadDocuments || []).length === 0 ? "Subir foto" : "Agregar otra foto"}
			</button>
			{
				(descargables || []).map((documento : any) =>
					<button key={"D_" + documento.id} type="button" disabled={idleLocked}
						onClick={() => onDescargar && onDescargar(documento)}
					>
						<FontAwesomeIcon icon={solid("circle-down")} />
						{String(documento.name || "").length <= 18 ? documento.name : "Foto de la clase"}
					</button>
				)
			}
		</div>

		<div className="waPaseGuardar">
			<span className="waPaseRecado">
				{recado}
			</span>
			<button type="button" disabled={!habilitado} onClick={onGuardar}>
				Guardar
			</button>
		</div>
	</div>
}

export default PaseDeLista
